import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import {
  useResource, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge,
  STATUS_TONE, fmtDate, fmtDateTime, fmtMoney, fmtDuration,
} from "../../components/app/Kit";

const TABS = ["Overview", "Activity", "Calls", "Messages", "Appointments", "Tasks", "Notes", "Payments", "Automation"];

const TYPE_TONE = {
  CALL: "cyan", SMS: "green", WHATSAPP: "green", EMAIL: "purple",
  APPOINTMENT: "cyan", PAYMENT: "amber", NOTE: "dim",
};

export default function CustomerProfile() {
  const { customerId } = useParams();
  const { data, state, error, reload } = useResource(`/customer-detail/${customerId}`);
  const [tab, setTab] = useState("Overview");
  const [note, setNote] = useState("");

  if (state === "loading") return <Loading rows={8} testId="customer-profile-loading" />;
  if (state === "error")
    return <ErrorState message={error} onRetry={reload} testId="customer-profile-error" />;

  const { customer, calls, messages, appointments, tasks, invoices, timeline, automation_history } = data;

  const saveNote = async () => {
    if (note.trim().length < 2) return;
    try {
      await api.patch(`/customers/${customer.id}`, { notes: note.trim() });
      toast.success("Note saved to the customer record.");
      setNote("");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="customer-profile-page">
      <Link to="/app/customers" data-testid="customer-back-link" className="inline-flex items-center gap-1.5 text-xs text-elv-dim hover:text-elv-text">
        <Icons.ArrowLeft className="h-3.5 w-3.5" /> All customers
      </Link>

      <header className="mt-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 data-testid="customer-profile-name" className="text-2xl font-semibold text-elv-text sm:text-3xl">
            {customer.name}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Pill tone={STATUS_TONE[customer.status]} testId="customer-status-pill">
              {customer.status}
            </Pill>
            <span className="font-mono text-[11px] text-elv-dim">lead score {customer.lead_score}</span>
            <span className="text-[11px] text-elv-dim">Owner: {customer.assigned_to}</span>
            {customer.tags?.map((t) => (
              <Pill key={t} tone="purple">
                {t}
              </Pill>
            ))}
          </div>
        </div>
        <DemoBadge />
      </header>

      <div className="scrollbar-thin mt-7 flex gap-1.5 overflow-x-auto border-b border-elv-line pb-2">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            data-testid={`customer-tab-${t.toLowerCase()}`}
            className={`whitespace-nowrap rounded-md px-3 py-2 text-[12px] transition-colors duration-200 ${
              tab === t ? "bg-elv-green/[0.09] text-elv-green" : "text-elv-dim hover:text-elv-text"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-4">
        {tab === "Overview" ? (
          <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
            <Panel testId="customer-details" title="Details">
              <dl className="grid gap-3 sm:grid-cols-2">
                {[
                  ["Name", customer.name], ["Phone", customer.phone], ["Email", customer.email],
                  ["Company", customer.company], ["Lead source", customer.source], ["Status", customer.status],
                  ["Assigned staff", customer.assigned_to], ["Lead score", customer.lead_score],
                  ["Revenue", fmtMoney(customer.revenue)], ["Payment status", customer.payment_status],
                  ["Created", fmtDate(customer.created_at)], ["Last contact", fmtDate(customer.last_contact)],
                  ["Next follow-up", fmtDate(customer.next_follow_up)],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] uppercase tracking-[0.16em] text-elv-dim">{k}</dt>
                    <dd className="mt-1 text-xs text-elv-text">{String(v ?? "—")}</dd>
                  </div>
                ))}
              </dl>
            </Panel>
            <Panel testId="customer-counts" title="Engagement">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[
                  ["Calls", calls.length], ["Messages", messages.length],
                  ["Appointments", appointments.length], ["Tasks", tasks.length],
                  ["Invoices", invoices.length], ["Timeline events", timeline.length],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-elv-dim">{k}</p>
                    <p className="mt-2 font-display text-xl text-elv-text">{v}</p>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        ) : null}

        {tab === "Activity" ? (
          <Panel testId="customer-timeline" title="Activity timeline">
            {timeline.length === 0 ? (
              <EmptyState icon="Activity" title="No activity yet" sub="Calls, messages and bookings will appear here." testId="customer-timeline-empty" />
            ) : (
              <ol className="grid gap-3">
                {timeline.map((t, i) => (
                  <li key={i} data-testid={`timeline-item-${i}`} className="flex items-start gap-3 border-l border-elv-line pl-4">
                    <span className="-ml-[21px] mt-1.5 h-2 w-2 shrink-0 rounded-full bg-elv-green" />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Pill tone={TYPE_TONE[t.type] || "dim"}>{t.type}</Pill>
                        <span className="font-mono text-[10px] text-elv-dim">{fmtDateTime(t.at)}</span>
                      </div>
                      <p className="mt-1.5 text-xs text-elv-dim">{t.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        ) : null}

        {tab === "Calls" ? (
          <Panel testId="customer-calls" title="Calls">
            {calls.length === 0 ? (
              <EmptyState icon="PhoneCall" title="No calls" sub="Your AI receptionist activity will appear here." testId="customer-calls-empty" />
            ) : (
              <ul className="grid gap-2">
                {calls.map((c) => (
                  <li key={c.id} data-testid={`customer-call-${c.id}`} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs text-elv-text">
                        {c.direction} · {fmtDuration(c.duration_seconds)}
                      </span>
                      <div className="flex items-center gap-2">
                        <Pill tone={c.handled_by === "ai" ? "green" : "dim"}>{c.handled_by}</Pill>
                        <Pill tone={STATUS_TONE[c.status] || "dim"}>{c.status}</Pill>
                      </div>
                    </div>
                    <p className="mt-2 text-[11px] text-elv-dim">{c.summary}</p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ) : null}

        {tab === "Messages" ? (
          <Panel testId="customer-messages" title="Messages">
            {messages.length === 0 ? (
              <EmptyState icon="MessagesSquare" title="No conversations" sub="Your AI receptionist activity will appear here." testId="customer-messages-empty" />
            ) : (
              <ul className="grid gap-2">
                {messages.map((m) => (
                  <li key={m.id} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs text-elv-text">{m.sender}</span>
                      <div className="flex items-center gap-2">
                        <Pill tone="cyan">{m.channel}</Pill>
                        <span className="font-mono text-[10px] text-elv-dim">{fmtDateTime(m.created_at)}</span>
                      </div>
                    </div>
                    <p className="mt-2 text-[11px] text-elv-dim">{m.body}</p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ) : null}

        {tab === "Appointments" ? (
          <Panel testId="customer-appointments" title="Appointments">
            {appointments.length === 0 ? (
              <EmptyState icon="CalendarCheck" title="No appointments" sub="Connect your calendar to start booking automatically." testId="customer-appointments-empty" />
            ) : (
              <ul className="grid gap-2">
                {appointments.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                    <span className="text-xs text-elv-text">
                      {a.service} · {fmtDateTime(a.start)}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-elv-dim">{a.staff}</span>
                      <Pill tone={STATUS_TONE[a.status] || "dim"}>{a.status}</Pill>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ) : null}

        {tab === "Tasks" ? (
          <Panel testId="customer-tasks" title="Tasks">
            {tasks.length === 0 ? (
              <EmptyState icon="ListChecks" title="No tasks" sub="Tasks created by automations or your team appear here." testId="customer-tasks-empty" />
            ) : (
              <ul className="grid gap-2">
                {tasks.map((t) => (
                  <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                    <span className="text-xs text-elv-text">{t.title}</span>
                    <div className="flex items-center gap-2">
                      <Pill tone={t.priority === "high" ? "red" : t.priority === "medium" ? "amber" : "dim"}>{t.priority}</Pill>
                      <Pill tone={STATUS_TONE[t.status] || "dim"}>{t.status}</Pill>
                      <span className="font-mono text-[10px] text-elv-dim">{fmtDate(t.due_at)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ) : null}

        {tab === "Notes" ? (
          <Panel testId="customer-notes" title="Notes">
            <p className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-xs text-elv-dim">
              {customer.notes || "No notes on this record yet."}
            </p>
            <div className="mt-4 grid gap-3">
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note to this record…"
                data-testid="customer-note-input"
                className="w-full rounded-md border border-elv-line bg-white/[0.02] p-3 text-xs text-elv-text outline-none focus:border-elv-green/40"
              />
              <button
                type="button"
                onClick={saveNote}
                data-testid="customer-note-save"
                className="w-fit rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]"
              >
                Save note
              </button>
            </div>
          </Panel>
        ) : null}

        {tab === "Payments" ? (
          <Panel testId="customer-payments" title="Payments">
            {invoices.length === 0 ? (
              <EmptyState
                icon="CreditCard"
                title="No invoices"
                sub="Stripe is not connected, so invoices are recorded manually for now."
                testId="customer-payments-empty"
              />
            ) : (
              <ul className="grid gap-2">
                {invoices.map((i) => (
                  <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                    <span className="font-mono text-xs text-elv-text">{i.number}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-elv-text">{fmtMoney(i.amount)}</span>
                      <Pill tone={STATUS_TONE[i.status] || "dim"}>{i.status}</Pill>
                      <span className="font-mono text-[10px] text-elv-dim">{fmtDate(i.issued_at)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ) : null}

        {tab === "Automation" ? (
          <Panel testId="customer-automation" title="Automation history">
            {automation_history.length === 0 ? (
              <EmptyState icon="Workflow" title="No automation runs" sub="Enable an automation to see execution history here." testId="customer-automation-empty" />
            ) : (
              <ul className="grid gap-2">
                {automation_history.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                    <span className="text-xs text-elv-text">{r.automation_name}</span>
                    <div className="flex items-center gap-2">
                      <Pill tone={r.result === "success" ? "green" : "dim"}>{r.result}</Pill>
                      <span className="font-mono text-[10px] text-elv-dim">{fmtDateTime(r.created_at)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ) : null}
      </div>
    </div>
  );
}
